/* eslint-disable array-bracket-newline */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { colors } from 'syngenta-digital-cropwise-react-ui-kit';

import EnvironmentStressBarLabel, { BarChartLabelProps } from './BarChartLabel';
import { roundedRectangle } from 'utils/roundedRectangle';
import { StyledSvg, StyledContainer, TooltipYearsContainer } from './BarChart.styles';
import { useBreakpoint } from 'hooks';
import { NumberValue } from 'd3';
import {
  StyledTemperatureContainer,
  StyledIconThermostat,
  StyledPrecipitationContainer,
  StyledTemperatureTitleContainer,
  StyledIconDropletSize,
  StyledTemperatureBoldText,
} from './BarChartLabel/BarChartLabel.styles';
import IconInfo from 'components/Icons/IconInfo';
import { useTranslation } from 'react-i18next';

type D3Selection = d3.Selection<d3.BaseType, unknown, null, undefined>;

// d3 selectors for the hover guide elements appended in the chart-draw effect.
const HOVER_GUIDE_LINE_SELECTOR = '.hover-guide-line';
const HOVER_GUIDE_LABEL_SELECTOR = '.hover-guide-label';

export interface BarChartProps {
  id?: string;
  width?: number;
  height?: number;
  yMin: number;
  yMax: number;
  yStep: number;
  yLabel: string;
  yLabelLeft?: string;
  yLabelRight?: string;
  className?: string;
  data: BarChartLabelProps[];
  areValuesDecimal?: boolean;
  groupBarsColors?: string[];
  isGroupBar?: boolean;
  distanceBetweenBars?: number;
  paddingLeft?: number;
  showTooltip?: boolean;
  showTitleOnTooltip?: boolean;
  isPdfView?: boolean;
  showLabels?: boolean;
  showYaxisToRight?: boolean;
  opacity?: number;
  showValuePercentage?: boolean;
  showHoverGuide?: boolean;
}

const BarChart = ({
  id,
  width = 1125,
  height = 450,
  yMin,
  yMax,
  yStep,
  data,
  yLabel,
  yLabelLeft,
  yLabelRight,
  className,
  areValuesDecimal = false,
  groupBarsColors,
  isGroupBar = false,
  distanceBetweenBars = 24,
  showTooltip = false,
  paddingLeft = 75,
  showTitleOnTooltip = true,
  isPdfView = false,
  showLabels = true,
  showYaxisToRight = false,
  opacity = 1,
  showValuePercentage = false,
  showHoverGuide = false,
}: BarChartProps): JSX.Element => {
  const { isMobile, landscape } = useBreakpoint();
  const [svgWidth, setSvgWidth] = useState(width);
  const [barWidth, setBarWidth] = useState(0);
  const [isWidthUpdated, setIsWidthUpdated] = useState(false);
  const [selectedBarId, setSelectedBarId] = useState(-1);
  const [isTablet, setIsTablet] = useState(false);
  const [infoTooltip, setInfoTooltip] = useState<{
    idx: number;
    text: string;
    position: { top: number; left: number };
  } | null>(null);

  useEffect(() => {
    const checkTablet = () => {
      setIsTablet(window.innerWidth < 1025);
    };
    checkTablet();
    window.addEventListener('resize', checkTablet);
    return () => {
      window.removeEventListener('resize', checkTablet);
    };
  }, []);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const paddingTop = 10;

  // Adjust padding for mobile
  const mobilePaddingLeft = isMobile ? 50 : paddingLeft;
  const [t] = useTranslation();

  const customLabelYAxisHeight = 120;
  const chartHeight = height - paddingTop - customLabelYAxisHeight;
  const yScale = useMemo(
    () => d3.scaleLinear().domain([yMin, yMax]).range([chartHeight, paddingTop]),
    [chartHeight, yMin, yMax]
  );

  const isProdEnv = process.env.REACT_APP_ENV === 'prod';
  const shouldShowHoverGuide = showHoverGuide && !isProdEnv;

  const getResponsiveBarWidth = (screenWidth: number) => {
    const baseWidth = 20;
    if (screenWidth < 480) {
      return Math.max(6, baseWidth * (screenWidth / 1200));
    } else if (screenWidth < 820) {
      return Math.max(12, baseWidth * (screenWidth / 1200));
    } else if (screenWidth < 1024) {
      return Math.max(17, baseWidth * (screenWidth / 1200));
    } else if (screenWidth < 1440) {
      return Math.max(19, baseWidth * (screenWidth / 1200));
    } else if (screenWidth < 1920) {
      return Math.max(20, baseWidth * (screenWidth / 1200));
    }
    return baseWidth;
  };

  const getResponsiveGapWidth = (screenWidth: number) => {
    const BASE_GAP_DEFAULT = 10;
    const BASE_GAP_MOBILE = 4;
    const BASE_GAP_TABLET = 6;
    const BASE_GAP_DESKTOP = 5;
    const SCREEN_WIDTH_MOBILE = 500;
    const SCREEN_WIDTH_TABLET = 798;
    const SCREEN_WIDTH_DESKTOP = 1024;

    let baseGap = BASE_GAP_DEFAULT;
    if (screenWidth < SCREEN_WIDTH_MOBILE) {
      // Reduce gap for mobile to give more space to bars
      baseGap = BASE_GAP_MOBILE;
      return Math.max(1, baseGap * (screenWidth / 1200));
    } else if (screenWidth < SCREEN_WIDTH_TABLET) {
      baseGap = BASE_GAP_TABLET;
      return Math.max(2, baseGap * (screenWidth / 1200));
    } else if (screenWidth < SCREEN_WIDTH_DESKTOP) {
      baseGap = BASE_GAP_DESKTOP;
      return Math.max(5, baseGap * (screenWidth / 1200));
    }
    return baseGap;
  };

  // Adjust distance between bars for mobile
  const responsiveDistanceBetweenBars = isMobile
    ? Math.max(10, distanceBetweenBars / 2)
    : distanceBetweenBars;

  const processString = (x: string): string[] => {
    if (x.length < 34) {
      return [x];
    }
    const words = x.split(' ');
    if (words.length > 3) {
      return [words.slice(0, 3).join(' '), words.slice(3).join(' ')];
    } else if (words.length === 3) {
      return [words.slice(0, 2).join(' '), words[2]];
    }
    return [
      x.slice(0, 30).trim() + (x[30] !== ' ' && x[29] !== ' ' ? '-' : ''),
      x.slice(30).trim(),
    ];
  };

  const formatHoverGuideValue = useCallback(
    (value: NumberValue): string => {
      if (areValuesDecimal) {
        return Number(value).toFixed(2);
      }

      return String(value);
    },
    [areValuesDecimal]
  );

  // Create bar chart
  useEffect(() => {
    // Create root container where we will append all other chart elements
    const svgEl = d3.select(svgRef.current);
    // Clear svg content before adding new elements
    svgEl.selectAll('*').remove();

    // Create the y axes (both left and right)
    const getDecimalValue = (d: d3.NumberValue): string => {
      if (areValuesDecimal) {
        return String((d as number).toFixed(2));
      }
      return String(d);
    };

    const yAxisLeft = d3
      .axisLeft(yScale)
      .ticks(Math.floor((yMax - yMin) / yStep) + 1)
      .tickValues(d3.range(yMin, yMax + yStep, yStep))
      .tickFormat((d: d3.NumberValue, idx) => {
        if (showYaxisToRight) {
          return '';
        }
        return idx % 2 === 0 ? getDecimalValue(d) : '';
      });

    // const yAxisRight = d3
    //   .axisRight(yScale)
    //   .ticks(Math.floor((yMax - yMin) / yStep) + 1)
    //   .tickValues(d3.range(yMin, yMax + yStep, yStep))
    //   .tickFormat((d: d3.NumberValue, idx) => {
    //     if (showYaxisToRight) {
    //       return idx % 2 === 0 ? getDecimalValue(d) : '';
    //     }
    //     return '';
    //   });

    // Append the left y axis to the SVG element
    const yAxisLeftGroup = svgEl
      .append('g')
      .attr('class', 'y-axis-left')
      .attr('transform', `translate(${mobilePaddingLeft},0)`)
      .call(yAxisLeft);

    // Append the right y axis to the SVG element
    // const yAxisRightGroup = svgEl
    //   .append('g')
    //   .attr('class', 'y-axis-right')
    //   .attr('transform', `translate(${svgWidth - mobilePaddingLeft},0)`)
    //   .call(yAxisRight);

    // Hide y-axis lines for both axes
    yAxisLeftGroup.selectAll('.tick').select('line').style('display', 'none');
    // yAxisRightGroup.selectAll('.tick').select('line').style('display', 'none');

    // Add y axis label for left axis
    if (yLabelLeft || (!showYaxisToRight && yLabel)) {
      const leftAxisLabel = processString(yLabelLeft || yLabel);
      yAxisLeftGroup
        .append('text')
        .attr('class', 'y-axis-label')
        .attr('x', (chartHeight * -1) / 2)
        .attr('y', 13 - mobilePaddingLeft)
        .text(leftAxisLabel[0]);
      if (leftAxisLabel.length > 1) {
        yAxisLeftGroup
          .append('text')
          .attr('class', 'y-axis-label')
          .attr('x', (chartHeight * -1) / 2)
          .attr('y', 31 - mobilePaddingLeft)
          .text(leftAxisLabel[1]);
      }
    }

    // Add y axis label for right axis
    // if (yLabelRight || (showYaxisToRight && yLabel)) {
    //   const rightAxisLabel = processString(yLabelRight || yLabel);
    //   yAxisRightGroup
    //     .append('text')
    //     .attr('class', 'y-axis-label')
    //     .attr('x', (chartHeight * -1) / 2)
    //     .attr('y', mobilePaddingLeft - 13)
    //     .text(rightAxisLabel[0]);
    //   if (rightAxisLabel.length > 1) {
    //     yAxisRightGroup
    //       .append('text')
    //       .attr('class', 'y-axis-label')
    //       .attr('x', (chartHeight * -1) / 2)
    //       .attr('y', mobilePaddingLeft - 31)
    //       .text(rightAxisLabel[1]);
    //   }
    // }
    // Add horizontal lines spanning between both y-axes
    svgEl
      .selectAll('.y-axis-left .tick')
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', svgWidth - mobilePaddingLeft * 2)
      .attr('y', 0)
      .attr('stroke-width', '1px')
      .attr('stroke', (_, i) => (i % 2 === 1 ? colors.neutral30 : colors.neutral40))
      .attr('stroke-dasharray', (_, i) => (i % 2 === 1 ? '3 3' : null));

    svgEl
      .append('line')
      .attr('class', 'hover-guide-line')
      .attr('data-testid', 'bar-hover-guide')
      .attr('x1', mobilePaddingLeft)
      .attr('x2', svgWidth - mobilePaddingLeft)
      .attr('y1', yScale(0))
      .attr('y2', yScale(0))
      .attr('stroke', colors.neutral60)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4 4')
      .style('opacity', 0)
      .style('pointer-events', 'none');

    const hoverGuideLabel = svgEl
      .append('g')
      .attr('class', 'hover-guide-label')
      .style('opacity', 0)
      .style('pointer-events', 'none');

    hoverGuideLabel
      .append('rect')
      .attr('class', 'hover-guide-label-bg')
      .attr('rx', 4)
      .attr('ry', 4)
      .attr('fill', colors.neutral90);

    hoverGuideLabel
      .append('text')
      .attr('class', 'hover-guide-label-text')
      .attr('text-anchor', 'end')
      .attr('fill', colors.neutral00)
      .attr('font-size', 12)
      .attr('font-weight', 600)
      .attr('dominant-baseline', 'middle');

    const marginStart = isMobile ? 0 : 5; // 5px space at the start
    const marginEnd = isMobile ? 0 : 5; // 5px space at the end
    const totalGapSpace = (data.length - 1) * responsiveDistanceBetweenBars; // Total space for gaps between bars
    const responsiveGroupBarWidth = getResponsiveBarWidth(svgWidth);
    const responsiveDistanceBetweenSubgroupsBars = getResponsiveGapWidth(svgWidth);
    const borderRadius = 4;
    const chartAreaWidth = svgWidth - mobilePaddingLeft * 2; // Space between Y-axes
    const availableWidth = chartAreaWidth - marginStart - marginEnd - totalGapSpace;
    const calculatedBarWidth = availableWidth / data.length;
    const maxBarWidth = availableWidth / 2;
    const newBarWidth = Math.min(calculatedBarWidth, maxBarWidth);

    const totalUsedWidth = newBarWidth * data.length + totalGapSpace;
    const centeringOffset = (chartAreaWidth - totalUsedWidth) / 2;
    setBarWidth(newBarWidth);

    // Create the bars - positioned between the Y-axes with proper spacing
    svgEl
      .append('g')
      .attr('class', 'bars')
      .selectAll('g')
      .data(data)
      .enter()
      .append('g')
      .attr('transform', (_, i) => {
        // Position each bar with centering offset
        const xPosition =
          mobilePaddingLeft + centeringOffset + i * (newBarWidth + responsiveDistanceBetweenBars);
        return `translate(${xPosition}, 0)`;
      })
      .attr('id', (_, i) => `${id}-bar-${i}`)
      .attr('fill', (d) => d.barColor)
      .selectAll('g')
      .data((d) => (Array.isArray(d.value) ? d.value : [d.value]))
      .enter()
      .append('path')
      .attr('class', 'bar')
      .attr('d', (d, i, nodes) => {
        const y = d < 0 ? yScale(0) : yScale(d);
        let x = 0;
        const barHeight = d === 0 ? 0 : Math.abs(yScale(d) - yScale(0));

        if (i > 0) {
          x += (responsiveGroupBarWidth + responsiveDistanceBetweenSubgroupsBars) * i;
        }

        // center group bars
        if (isGroupBar) {
          x += newBarWidth / 2;
          x -=
            (responsiveGroupBarWidth * nodes.length +
              responsiveDistanceBetweenSubgroupsBars * (nodes.length - 1)) /
            2;
        }

        return roundedRectangle(
          x,
          y,
          isGroupBar ? responsiveGroupBarWidth : newBarWidth,
          barHeight,
          borderRadius,
          d >= 0,
          d >= 0,
          d < 0,
          d < 0
        );
      })
      .attr('fill', (yValue, i) => (groupBarsColors ? groupBarsColors[i] : null))
      .style('opacity', (d) => (d === 0 ? 0 : opacity));
  }, [
    width,
    height,
    yMin,
    yMax,
    yStep,
    yLabel,
    yLabelLeft,
    yLabelRight,
    svgWidth,
    paddingTop,
    mobilePaddingLeft,
    responsiveDistanceBetweenBars,
    data,
    areValuesDecimal,
    groupBarsColors,
    isGroupBar,
    chartHeight,
    yScale,
    id,
    showYaxisToRight,
    opacity,
    isMobile,
  ]);

  useEffect(() => {
    if (data) {
      const tooltip = d3.select(`#${id}`).select('.tooltip');
      const tooltipArrow = d3.select(`#${id}`).select('.tooltip-arrow');
      const hoverGuideLine = d3.select(svgRef.current).select(HOVER_GUIDE_LINE_SELECTOR);
      const hoverGuideLabel = d3.select(svgRef.current).select(HOVER_GUIDE_LABEL_SELECTOR);
      tooltip.style('opacity', 0);
      tooltipArrow.style('opacity', 0);
      hoverGuideLine.style('opacity', 0);
      hoverGuideLabel.style('opacity', 0);
      setSelectedBarId(-1);
    }
  }, [data, id]);

  useEffect(() => {
    const bars = d3.select(svgRef.current).selectAll('.bar');
    const hoverGuideLine = d3.select(svgRef.current).select(HOVER_GUIDE_LINE_SELECTOR);
    const hoverGuideLabel = d3.select(svgRef.current).select(HOVER_GUIDE_LABEL_SELECTOR);
    const hoverGuideLabelText = hoverGuideLabel.select('.hover-guide-label-text');
    const hoverGuideLabelBg = hoverGuideLabel.select('.hover-guide-label-bg');

    function handleShowHoverGuide(_: Event, datum: unknown) {
      if (!shouldShowHoverGuide) {
        return;
      }

      if (typeof datum !== 'number') {
        return;
      }

      const yPosition = yScale(datum);
      const formattedValue = formatHoverGuideValue(datum);

      hoverGuideLine
        .attr('x1', mobilePaddingLeft)
        .attr('x2', svgWidth - mobilePaddingLeft)
        .attr('y1', yPosition)
        .attr('y2', yPosition)
        .style('opacity', 1);

      hoverGuideLabel
        .attr('transform', `translate(${svgWidth - 8}, ${yPosition})`)
        .style('opacity', 1);

      hoverGuideLabelText.text(formattedValue);

      const textNode = hoverGuideLabelText.node() as SVGTextElement | null;
      if (textNode) {
        const textBox = textNode.getBBox();
        hoverGuideLabelBg
          .attr('x', textBox.x - 6)
          .attr('y', textBox.y - 3)
          .attr('width', textBox.width + 12)
          .attr('height', textBox.height + 6);
      }
    }

    function handleHideHoverGuide() {
      if (!shouldShowHoverGuide) {
        return;
      }

      hoverGuideLine.style('opacity', 0);
      hoverGuideLabel.style('opacity', 0);
    }

    bars.on('mouseenter.hover-guide', handleShowHoverGuide);
    bars.on('mousemove.hover-guide', handleShowHoverGuide);
    bars.on('mouseleave.hover-guide', handleHideHoverGuide);

    return () => {
      bars.on('mouseenter.hover-guide', null);
      bars.on('mousemove.hover-guide', null);
      bars.on('mouseleave.hover-guide', null);
    };
  }, [formatHoverGuideValue, mobilePaddingLeft, shouldShowHoverGuide, svgWidth, yScale]);

  // Tooltip handler
  useEffect(() => {
    const bars = d3.select(svgRef.current).selectAll('.bar');
    const tooltip = d3.select(`#${id}`).select('.tooltip');
    const tooltipArrow = d3.select(`#${id}`).select('.tooltip-arrow');
    const hoverGuideLine = d3.select(svgRef.current).select(HOVER_GUIDE_LINE_SELECTOR);
    const hoverGuideLabel = d3.select(svgRef.current).select(HOVER_GUIDE_LABEL_SELECTOR);
    const hoverGuideLabelText = hoverGuideLabel.select('.hover-guide-label-text');
    const hoverGuideLabelBg = hoverGuideLabel.select('.hover-guide-label-bg');

    function handleClickBar(d: MouseEvent) {
      if (!svgRef.current || !d.target) {
        return;
      }

      const target = d.target as SVGElement;
      const parentNode = target.parentNode as SVGGElement;

      if (!parentNode || !parentNode.id) {
        return;
      }

      const idSplit = parentNode.id.split('-');
      const currentBarId = Number(idSplit[idSplit.length - 1]);

      // to remove the tooltip from screen
      if (selectedBarId === currentBarId) {
        tooltip.style('opacity', 0);
        tooltipArrow.style('opacity', 0);
        hoverGuideLine.style('opacity', 0);
        hoverGuideLabel.style('opacity', 0);
        return setSelectedBarId(-1);
      }

      const xPositionBar = parentNode.transform.baseVal.getItem(0).matrix.e;
      const barBoundingClient = target.getBoundingClientRect();
      const currentBarWidth = barBoundingClient.width;
      const currentBarHeight = barBoundingClient.height;
      const svgMarginTop = parseFloat(window.getComputedStyle(svgRef.current).marginTop);
      const bottomDefaultDistance = 15;
      const tooltipArrowHeight = 7;
      const svgHeight = height - customLabelYAxisHeight;
      const tooltipArrowPositionX = Number(xPositionBar) + Number(currentBarWidth / 2);

      let xPosition = Number(xPositionBar) + Number(currentBarWidth / 2);
      const yPosition = yScale(d3.select(target).datum() as NumberValue);

      // avoid tooltip going offscreen
      if (currentBarId === data.length - 1) {
        xPosition -= 62;
      }
      if (currentBarId === 0) {
        xPosition += 12;
      }

      setSelectedBarId(currentBarId);

      tooltip.style('opacity', 1);
      tooltipArrow.style('opacity', 1);
      if (shouldShowHoverGuide) {
        const formattedValue = formatHoverGuideValue(d3.select(target).datum() as NumberValue);

        hoverGuideLine
          .attr('x1', mobilePaddingLeft)
          .attr('x2', svgWidth - mobilePaddingLeft)
          .attr('y1', yPosition)
          .attr('y2', yPosition)
          .style('opacity', 1);

        hoverGuideLabel
          .attr('transform', `translate(${svgWidth - 8}, ${yPosition})`)
          .style('opacity', 1);

        hoverGuideLabelText.text(formattedValue);

        const textNode = hoverGuideLabelText.node() as SVGTextElement | null;
        if (textNode) {
          const textBox = textNode.getBBox();
          hoverGuideLabelBg
            .attr('x', textBox.x - 6)
            .attr('y', textBox.y - 3)
            .attr('width', textBox.width + 12)
            .attr('height', textBox.height + 6);
        }
      }

      if (yMin < 0) {
        const value = data[currentBarId] && (data[currentBarId].value as number);
        const yPositionTooltip = Math.ceil(svgHeight / 2) + currentBarHeight + tooltipArrowHeight;

        if (value > 0) {
          tooltip
            .style('top', 'unset')
            .style('bottom', `${yPositionTooltip - 1}px`)
            .style('left', `${xPosition}px`);
          tooltipArrow
            .style('left', `${tooltipArrowPositionX}px`)
            .style('top', `${Math.floor(svgHeight / 2) - currentBarHeight}px`)
            .style('transform', 'translate(-50%, 0)');
        } else {
          tooltip
            .style('top', `${yPositionTooltip}px`)
            .style('bottom', 'unset')
            .style('left', `${xPosition}px`);
          tooltipArrow
            .style('left', `${tooltipArrowPositionX}px`)
            .style('top', `${Math.ceil(svgHeight / 2) + currentBarHeight}px`)
            .style('transform', 'translate(-50%, 0) rotate(180deg)');
        }
      } else {
        tooltip
          .style('bottom', `${currentBarHeight + bottomDefaultDistance + tooltipArrowHeight}px`)
          .style('left', `${xPosition}px`);
        tooltipArrow
          .style('left', `${tooltipArrowPositionX}px`)
          .style('top', `${svgMarginTop + yPosition - tooltipArrowHeight}px`);
      }
    }

    if (showTooltip) {
      // Add the event listener to the bars
      bars.on('click', handleClickBar);
    }
    return () => {
      bars.on('click', null);
    };
  }, [
    svgRef,
    id,
    barWidth,
    yScale,
    selectedBarId,
    data,
    showTooltip,
    yMin,
    yMax,
    isMobile,
    landscape,
    height,
    shouldShowHoverGuide,
    mobilePaddingLeft,
    svgWidth,
    formatHoverGuideValue,
  ]);

  const handleInfoClick = (item: BarChartLabelProps, idx: number, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    // For tablets, prevent the mouseLeave from immediately closing the tooltip
    if (isTablet && infoTooltip && infoTooltip.idx === idx) {
      setInfoTooltip(null);
      return;
    }

    const stressType = item.title;
    const yearsStr = item.years?.join(', ') || 'N/A';
    const percentage = Number(Array.isArray(item.value) ? item.value[0] ?? 0 : item.value);
    const seasons = 20;
    const occurrence = Math.round((percentage / 100) * seasons);

    const tooltipText = t('Stress Info tooltip', {
      stressType: stressType,
      occurrence: occurrence,
      seasons: seasons,
      percentage: percentage,
      yearsStr: yearsStr,
    });
    setInfoTooltip({
      idx,
      text: tooltipText,
      position: {
        top: rect.top + window.scrollY - 10,
        left: rect.left + rect.width / 2,
      },
    });
  };
  // Adapt table on resize
  useEffect(() => {
    const updateWidth = () => {
      if (svgRef.current) {
        const parent = getComputedStyle(svgRef.current.parentNode?.parentNode as HTMLElement);
        const containerStyles = getComputedStyle(svgRef.current.parentNode as HTMLElement);
        const parentpaddingLeft = parseInt(parent.getPropertyValue('padding-left'), 10);
        const parentPaddingRight = parseInt(parent.getPropertyValue('padding-right'), 10);
        const parentMarginLeft = parseInt(containerStyles.getPropertyValue('margin-left'), 10);
        const parentWidth = parseInt(parent.getPropertyValue('width'), 10);

        // Calculate new SVG width with adjustments for mobile
        let newSvgWidth = parentWidth - parentpaddingLeft - parentPaddingRight - parentMarginLeft;

        // For mobile, ensure we have minimum width for the chart
        if (isMobile) {
          // Ensure minimum width on mobile
          newSvgWidth = Math.max(newSvgWidth, 320);

          // If we have multiple bars and are on a small screen, expand further
          if (data.length > 1 && newSvgWidth < 400) {
            newSvgWidth = Math.min(400, parentWidth);
          }
        }

        setSvgWidth(newSvgWidth);

        // Recalculate bar width and positions when container size changes
        const chartAreaWidth = newSvgWidth - (isMobile ? mobilePaddingLeft : paddingLeft) * 2;
        const marginStart = isMobile ? 0 : 5;
        const marginEnd = isMobile ? 0 : 5;
        const totalGapSpace =
          (data.length - 1) * (isMobile ? responsiveDistanceBetweenBars : distanceBetweenBars);
        const newBarWidth =
          (chartAreaWidth - marginStart - marginEnd - totalGapSpace) / data.length;
        setBarWidth(newBarWidth);

        setTimeout(() => {
          setIsWidthUpdated(true);
        }, 0);
      }
    };

    updateWidth();

    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [
    height,
    width,
    data.length,
    distanceBetweenBars,
    paddingLeft,
    mobilePaddingLeft,
    responsiveDistanceBetweenBars,
    isMobile,
  ]);
  useEffect(() => {
    if (!infoTooltip) {
      return;
    }

    const handleClose = () => setInfoTooltip(null);

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Don't close if clicking on the tooltip itself or info icon
      if (
        target.closest('.info-tooltip') ||
        target.closest('[data-testid="infoIcon"]') ||
        target.closest('.pointer-cursor')
      ) {
        return;
      }

      // For tablets, add a small delay to prevent immediate closing
      if (isTablet) {
        setTimeout(() => {
          handleClose();
        }, 100);
      } else {
        handleClose();
      }
    };

    // For tablets, delay adding the event listener to prevent immediate closing
    const timeoutId = setTimeout(
      () => {
        document.addEventListener('click', handleOutsideClick, true);
      },
      isTablet ? 200 : 0
    );

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [infoTooltip, isMobile, isTablet]);

  return (
    <StyledContainer id={id} className={`svg-bar-chart ${className}`}>
      <StyledSvg
        data-testid="bar-chart"
        ref={svgRef}
        width={svgWidth}
        height={height - customLabelYAxisHeight}
      />
      {showValuePercentage && (
        <div
          style={{
            display: 'flex',
            paddingLeft: isMobile ? mobilePaddingLeft : mobilePaddingLeft + 5,
            paddingRight: 80,
            marginBottom: 15,
            justifyContent: 'center',
          }}
        >
          {data?.map((item, idx) => (
            <div
              key={`Percentage-${item.id}`}
              style={{
                width: barWidth,
                textAlign: 'center',
                marginRight:
                  idx === data.length - 1
                    ? isMobile
                      ? -31
                      : 0
                    : `${responsiveDistanceBetweenBars}px`,
                backgroundColor: item.barColor,
                borderRadius: '5px',
                padding: '5px',
                display: 'inline-block',
                opacity: 0.7,
                position: 'relative',
                fontWeight: 900,
              }}
              onClick={(e) => {
                if (isMobile || isTablet) {
                  handleInfoClick(item, idx, e);
                }
              }}
            >
              {!isMobile && (
                <IconInfo
                  data-testid="infoIcon"
                  className="pointer-cursor"
                  width={14}
                  height={14}
                  color={'#000000'}
                  style={{
                    position: 'absolute',
                    top: '7px',
                    right: '7px',
                  }}
                  {...(!isMobile &&
                    !isTablet && {
                    onMouseEnter: (e) => handleInfoClick(item, idx, e),
                    onMouseLeave: () => setInfoTooltip(null),
                  })}
                />
              )}
              {isTablet && !isMobile && (
                <IconInfo
                  data-testid="infoIcon"
                  className="pointer-cursor"
                  width={14}
                  height={14}
                  color={'#000000'}
                  style={{
                    position: 'absolute',
                    top: '7px',
                    right: '7px',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInfoClick(item, idx, e);
                  }}
                />
              )}
              <span>{item.value}%</span>
            </div>
          ))}
        </div>
      )}

      {/* labels for pdf */}
      {isPdfView && showLabels && (
        <div
          style={{
            display: 'flex',
            paddingLeft: mobilePaddingLeft + 5, // 5px margin at start
            paddingRight: 5, // 5px margin at end
          }}
        >
          {data.map((item, idx) => (
            <EnvironmentStressBarLabel
              isPdfView={isPdfView}
              key={`Bar-${item.id}`}
              {...item}
              style={{
                width: barWidth,
                marginRight: idx === data.length - 1 ? 0 : `${responsiveDistanceBetweenBars}px`,
              }}
            />
          ))}
        </div>
      )}

      {isWidthUpdated && !isMobile && !isTablet && !isPdfView && showLabels && (
        <div
          id="BarChartLabel"
          style={{
            display: 'flex',
            paddingLeft: isMobile ? mobilePaddingLeft : mobilePaddingLeft + 5,
            paddingRight: 80,
            marginBottom: 15,
            justifyContent: 'center',
          }}
        >
          {data.map((item, idx) => (
            <EnvironmentStressBarLabel
              isPdfView={isPdfView}
              key={`Bar-${item.id}`}
              {...item}
              style={{
                width: barWidth,
                marginRight: idx === data.length - 1 ? 0 : `${responsiveDistanceBetweenBars}px`,
              }}
            />
          ))}
        </div>
      )}

      {showTooltip && (
        <>
          <div className="tooltip">
            {selectedBarId >= 0 && (
              <>
                {showTitleOnTooltip && (
                  <StyledTemperatureTitleContainer>
                    <StyledTemperatureBoldText>
                      {data[selectedBarId].title}
                    </StyledTemperatureBoldText>
                  </StyledTemperatureTitleContainer>
                )}
                <StyledTemperatureContainer>
                  <StyledIconThermostat color={colors.neutral00} />
                  <div>{data[selectedBarId].temperatureLabel}</div>
                </StyledTemperatureContainer>
                <StyledPrecipitationContainer>
                  <StyledIconDropletSize color={colors.neutral00} />
                  <div>{data[selectedBarId].precipitationLabel}</div>
                </StyledPrecipitationContainer>
                {data[selectedBarId].years && (
                  <TooltipYearsContainer>
                    {data[selectedBarId].years?.join(', ')}
                  </TooltipYearsContainer>
                )}
              </>
            )}
          </div>
          <div className="tooltip-arrow" />
        </>
      )}
      {infoTooltip && (
        <div
          data-testid="percentage-info-tooltip"
          className="info-tooltip"
          style={{
            position: 'fixed',
            top: infoTooltip.position.top,
            left: infoTooltip.position.left,
            transform: 'translate(-50%, -100%)',
            background: '#333',
            color: '#fff',
            padding: '8px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            width: isMobile ? '180px' : '250px',
            minHeight: isMobile ? '50px' : '60px',
            zIndex: 9999,
            textAlign: 'center',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            boxSizing: 'border-box',
          }}
        >
          {infoTooltip.text}
          <div
            style={{
              position: 'absolute',
              bottom: '-5px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '5px solid #333',
              zIndex: 9999,
            }}
          />
        </div>
      )}
    </StyledContainer>
  );
};

export default BarChart;
